<script setup lang="ts">
    import { type CloudinaryImage } from '@cloudinary/url-gen/index';
    import { AdvancedImage, lazyload, placeholder, responsive } from '@cloudinary/vue';
    import { useMedia, useProfileImage } from '@shared/composables/useImage';
    import { type ImageTyping, type MediaTyping } from '@shared/typings';
    import { type ComputedRef } from 'vue';

    const props = defineProps<{
        media?: MediaTyping | null
        profileImage?: ImageTyping | null
        preview?: string | null
        aspectRatio?: string
        noCrop?: boolean
        assetWidth?: number
        thumbnail?: boolean
    }>();

    // Error handling for Cloudinary htmlPluginState issues
    const hasCloudinaryError = ref(false);
    const fallbackImageUrl = ref<string | null>(null);

    // Use different plugins for thumbnails vs full images
    const plugins = computed(() => {
        if (props.thumbnail) {
            // For thumbnails, only use lazyload without responsive to respect asset-width
            return [lazyload(), placeholder({ mode: 'blur' })];
        }

        // For full images, use responsive plugin
        return [
            lazyload(), responsive({
                steps: [
                    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                    800, 1000, 1400,
                ],
            }), placeholder({ mode: 'blur' }),
        ];
    });

    const processedMedia: ComputedRef<CloudinaryImage | null> = computed(() => {
        if (hasCloudinaryError.value) return null;
        return useMedia(
            props.media ?? null, props.aspectRatio, props.assetWidth, props.noCrop,
        );
    });

    const processedImage: ComputedRef<CloudinaryImage | null> = computed(() => {
        if (hasCloudinaryError.value) return null;
        return useProfileImage(props.profileImage ?? null);
    });

    // Generate fallback URL when Cloudinary fails
    const getFallbackUrl = (media: MediaTyping | null, profileImage: ImageTyping | null): string | null => {
        if (media?.path) {
            // Construct direct Cloudinary URL without plugins
            return `https://res.cloudinary.com/constell-dev/image/upload/${media.path}`;
        }
        if (profileImage?.path) {
            return `https://res.cloudinary.com/constell-dev/image/upload/${profileImage.path}`;
        }
        return null;
    };

    // Handle Cloudinary errors
    onErrorCaptured((error: Error) => {
        if (error.message.includes('htmlPluginState')) {
            hasCloudinaryError.value = true;
            fallbackImageUrl.value = getFallbackUrl(props.media ?? null, props.profileImage ?? null);
            return false; // Prevent error propagation
        }
        return true;
    });
</script>

<template>
    <div
        class="advanced-image"
        data-constell="constell-advanced-image"
    >
        <img
            v-if="preview"
            :src="preview"
            class="advanced-image__inner"
        >
        <img
            v-else-if="hasCloudinaryError && fallbackImageUrl"
            :src="fallbackImageUrl"
            class="advanced-image__inner"
        >
        <AdvancedImage
            v-else-if="media && !hasCloudinaryError"
            class="advanced-image__inner"
            :cld-img="processedMedia"
            :plugins="plugins"
        />
        <AdvancedImage
            v-else-if="profileImage && !hasCloudinaryError"
            class="advanced-image__inner"
            :cld-img="processedImage"
            :plugins="plugins"
        />
        <slot name="overlay" />
    </div>
</template>

<style scoped lang="scss">
    .advanced-image {
        display: flex;
        overflow: hidden;
        block-size: 100%;
        inline-size: 100%;

        &__inner {
            block-size: 100%;
            inline-size: 100%;
            object-fit: cover; // Ensures the image covers the entire container
            object-position: center; // Centers the image within the container
        }

        /* Style the overlay slot content */
        :deep(.btn-overlay-content) {
            position: absolute;
            z-index: 1;
            inset-block-end: var(--spacing-04);
            inset-inline-end: var(--spacing-07);
        }
    }
</style>
